import React from "react";
import { Loader2, Trophy } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import ConfirmDialog from "@/components/ConfirmDialog";
import { useJobs } from "@/contexts/JobsContext";

// "Find Top Performers" — dry-runs the award-list search (cost preview),
// then launches a background job that searches brokerage award pages +
// local "top realtor" roundups and badges name-matched realtors.
export default function FindTopPerformersButton() {
  const [plan, setPlan] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const { activeJobs, track: trackJob } = useJobs();
  const running = activeJobs.some((j) => j.type === "find_top_performers");

  const planIt = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/realtors/find-top-performers", { dry_run: true });
      setPlan(data);
      setOpen(true);
    } catch (e) {
      toast.error("Cost preview failed", { description: formatApiError(e) });
    } finally {
      setBusy(false);
    }
  };

  const launch = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/realtors/find-top-performers", { dry_run: false });
      if (data.job_id) {
        trackJob(data.job_id, {
          type: "find_top_performers",
          label: `Top performers · ${data.city}`,
        });
      }
      toast.success("Top-performer search started", {
        description: `Searching award lists for ${data.city} — matches get a gold badge with a source link.`,
      });
      setOpen(false);
      setPlan(null);
    } catch (e) {
      toast.error("Launch failed", { description: formatApiError(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={planIt}
        disabled={busy || running}
        className="h-8 px-2.5 text-xs"
        data-testid="find-top-performers-button"
        title="Search brokerage award lists (RE/MAX Hall of Fame, Royal LePage top producers…) and badge matched realtors"
      >
        {busy || running ? (
          <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
        ) : (
          <Trophy className="h-3.5 w-3.5 mr-1.5 text-amber-500" />
        )}
        {running ? "Searching…" : "Find Top Performers"}
      </Button>
      <ConfirmDialog
        open={open}
        onClose={() => setOpen(false)}
        onConfirm={launch}
        title="Search for award-winning realtors?"
        description={
          plan ? (
            <div className="text-sm space-y-2">
              <p>
                Runs <b>{plan.queries}</b> targeted searches for <b>{plan.city}</b> —
                brokerage award pages (RE/MAX Hall of Fame, Royal LePage top producers,
                Century 21 Centurion…), local &quot;top realtor&quot; roundups, and
                association awards — then name-matches winners against your list.
              </p>
              <p>
                Estimated Firecrawl credits: <b>≤ {plan.estimated_credits}</b>
                {typeof plan.firecrawl_remaining === "number" ? (
                  <>
                    {" "}· remaining this month: <b>{plan.firecrawl_remaining}</b>
                  </>
                ) : null}
              </p>
              {!plan.ok_to_proceed ? (
                <p className="text-red-700 text-xs">
                  Not enough Firecrawl credits remaining. Raise the cap in Settings → Scraping,
                  or wait for the monthly rollover.
                </p>
              ) : (
                <p className="text-xs text-slate-500">
                  Matched realtors get a gold <b>Top</b> badge with a link to the source page so
                  you can verify each one. No profile data is overwritten.
                </p>
              )}
            </div>
          ) : (
            ""
          )
        }
        confirmLabel="Run search"
        confirmDisabled={!plan || !plan.ok_to_proceed}
        testid="find-top-performers-confirm"
      />
    </>
  );
}
